// AnnualLeaveSingleTable.tsx
import "./AnnualLeaveSingleTable.scss";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Table, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { FieldTimeOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { months } from "../../utils/constants";
import type {
  AnnualPlanItem,
  LeaveRequest,
  BankHolidays,
  User,
} from "../../utils/types";

type AnnualLeaveSingleTableProps = {
  annualLeaveYear: string;
  leaveRequests: LeaveRequest[];
  user: User | null;
  onDateClick: (user: User | null, date: Date) => void;
  selectedLeaveTypes: string[];
  bankHolidays: BankHolidays[];
  annualPlan: AnnualPlanItem[];
  loading?: boolean;
};

type CalendarRow = {
  key: string;
  employeeName: string;
  daysRemaining: number | string;
};

// Maps every request status to the legend toggle key that shows/hides it -
// mirrors CALENDAR_VISIBLE_STATUSES in MyCalendar.tsx and the PENDING/
// FINAL_APPROVED buckets in AnnualLeaveTable.tsx (team calendar).
const STATUS_BUCKET: Record<string, string> = {
  AWAITING_MANAGER: "PENDING",
  AWAITING_ADMINISTRATIV: "PENDING",
  RETURNED_FOR_CORRECTION: "PENDING",
  AWAITING_DOCUMENT_REVIEW: "PENDING",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  CANCELLED: "CANCELLED",
  CANCELLATION_REJECTED: "CANCELLED",
  AWAITING_MANAGER_CANCELLATION: "PENDING_CANCELLED",
  AWAITING_ADMINISTRATIV_CANCELLATION: "PENDING_CANCELLED",
};

const STATUS_CLASS: Record<string, string> = {
  PENDING: "leave-awaiting-manager",
  APPROVED: "leave-approved",
  REJECTED: "leave-rejected",
  CANCELLED: "leave-cancelled",
  PENDING_CANCELLED: "leave-pending-cancelled",
};

const weekday2 = (d: Date) =>
  d.toLocaleDateString("en-GB", { weekday: "short" }).slice(0, 2); // Mo, Tu, ...

const AnnualLeaveSingleTable = ({
  annualLeaveYear,
  user,
  leaveRequests,
  onDateClick,
  selectedLeaveTypes,
  bankHolidays,
  annualPlan,
  loading
}: AnnualLeaveSingleTableProps) => {
  const [collapsedMonths, setCollapsedMonths] = useState<
    Record<string, boolean>
  >({});
  const monthRefs = useRef<(HTMLTableCellElement | null)[]>([]);
  const currentMonthIndex = new Date().getMonth();

  const year = useMemo(() => parseInt(annualLeaveYear, 10), [annualLeaveYear]);
  const monthsList = useMemo(() => months(year), [year]);

  const toggleMonth = useCallback((monthName: string) => {
    setCollapsedMonths((prev) => ({
      ...prev,
      [monthName]: !prev[monthName],
    }));
  }, []);

  const leaveDayInfoMap = useMemo(() => {
    const map = new Map<
      string,
      { type: string; comment: string; substituteName: string; status: string }
    >();
    leaveRequests.forEach((lq) => {
      lq.leaveDays.forEach((day) => {
        map.set(day, {
          type: lq.requestType,
          comment: lq.comment ?? "",
          substituteName: lq.substituteName ?? "",
          status: lq.requestStatus,
        });
      });
    });
    return map;
  }, [leaveRequests]);

  const bankHolidayMap = useMemo(() => {
    return new Map(
      bankHolidays.map((h) => [
        new Date(h.date).toLocaleDateString("sv-SE"),
        h.description,
      ]),
    );
  }, [bankHolidays]);

  const annualPlanDates = useMemo(() => {
    const datesSet = new Set<string>();
    annualPlan.forEach(({ startDate, endDate }) => {
      const start = new Date(startDate);
      const end = new Date(endDate);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        datesSet.add(d.toISOString().split("T")[0]);
      }
    });
    return datesSet;
  }, [annualPlan]);

  const infoBtnMsg = `Calculation:
(Current year days + Previous year days) -
Used current year days - Used previous year days`;

  const getCellMeta = useCallback(
    (date: Date, formattedDate: string) => {
      const dayOfWeek = date.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

      const leaveInfo = leaveDayInfoMap.get(formattedDate);
      const leaveType = leaveInfo?.type;
      const status = leaveInfo?.status;
      const statusBucket = status ? STATUS_BUCKET[status] : undefined;

      const bankHolidayDescription = bankHolidayMap.get(formattedDate);
      const isBankHoliday =
        !!bankHolidayDescription && selectedLeaveTypes.includes("BANK_HOLIDAY");

      const isAnnualPlanDay =
        annualPlanDates.has(formattedDate) &&
        selectedLeaveTypes.includes("ANNUAL_PLAN");

      const shouldHideBecauseStatus =
        !!statusBucket && !selectedLeaveTypes.includes(statusBucket);

      if (shouldHideBecauseStatus && !isAnnualPlanDay) {
        return {
          className: "month-days hidden-cell",
          popupContent: "",
          onClick: undefined as undefined | (() => void),
          cursor: "default",
        };
      }

      const showPopupForStatus =
        !!statusBucket && selectedLeaveTypes.includes(statusBucket);

      const isLeaveDay = !!leaveType && selectedLeaveTypes.includes(leaveType);

      let popupContent = "";
      if (isBankHoliday) popupContent = `Holiday: ${bankHolidayDescription}`;
      else if (showPopupForStatus || isLeaveDay)
        popupContent = `${leaveInfo?.type} - ${
          leaveInfo?.comment || "No comment"
        } | Substitute: ${leaveInfo?.substituteName || "N/A"} | Status: ${status}`;
      else if (isWeekend) popupContent = "Weekend";
      else if (isAnnualPlanDay) popupContent = "Annual Plan Day";

      const statusClass = statusBucket ? STATUS_CLASS[statusBucket] : "";

      const className = [
        "month-days",
        isBankHoliday ? "BANK_HOLIDAY" : "",
        isWeekend ? "weekend" : "",
        statusClass,
        isLeaveDay ? `leave-${leaveType}` : "",
        isAnnualPlanDay ? "annual-plan-day" : "",
        shouldHideBecauseStatus && isAnnualPlanDay ? "muted-hidden-status" : "",
      ]
        .filter(Boolean)
        .join(" ");

      const allowClick = !isWeekend && !isLeaveDay && !isBankHoliday;

      return {
        className,
        popupContent,
        onClick: allowClick ? () => onDateClick(user, date) : undefined,
        cursor: allowClick ? "pointer" : "default",
      };
    },
    [
      annualPlanDates,
      bankHolidayMap,
      leaveDayInfoMap,
      onDateClick,
      selectedLeaveTypes,
      user,
    ],
  );

  const columns = useMemo<ColumnsType<CalendarRow>>(() => {
    const baseColumns: ColumnsType<CalendarRow> = [
      {
        title: <div className="calendar-name-container">Name</div>,
        dataIndex: "employeeName",
        key: "employeeName",
        width: 200,
        fixed: "left",
        className: "calendar-name",
        onHeaderCell: () => ({ className: "calendar-name" }),
        onCell: () => ({ className: "calendar-name" }),
      },
      {
        title: (
          <Tooltip
            title={
              <>
                <div className="remaining-days-tooltip-title">
                  Remaining annual leave days
                </div>
                <span className="info-tooltip">{infoBtnMsg}</span>
              </>
            }
          >
            <div className="remaining-days-header">
              <FieldTimeOutlined />
              <span>Remaining</span>
            </div>
          </Tooltip>
        ),
        dataIndex: "daysRemaining",
        key: "daysRemaining",
        width: 60,
        fixed: "left",
        className: "days-remaining-cell",
        onHeaderCell: () => ({ className: "remaining-days-header-cell" }),
        onCell: () => ({ className: "days-remaining-cell" }),
      },
    ];

    const monthColumns: ColumnsType<CalendarRow> = monthsList.map(
      (month, monthIndex) => {
        const isCollapsed = collapsedMonths[month.name];
        const LeadingArrow = isCollapsed ? LeftOutlined : RightOutlined;
        const TrailingArrow = isCollapsed ? RightOutlined : LeftOutlined;

        const monthTitle = (
          <div className="month-title">
            <LeadingArrow className="calendar-arrow" />
            <span>{month.name}</span>
            <TrailingArrow className="calendar-arrow" />
          </div>
        );

        if (isCollapsed) {
          return {
            title: monthTitle,
            key: `month-${month.name}`,
            onHeaderCell: () => ({
              className: "months",
              onClick: () => toggleMonth(month.name),
            }),
            children: [
              {
                title: `1–${month.days}`,
                key: `collapsed-${month.name}`,
                width: 24,
                onHeaderCell: () => ({
                  className: "month-days collapsed month-start month-end",
                  ref: (el: HTMLTableCellElement | null) => {
                    monthRefs.current[monthIndex] = el;
                  },
                }),
                onCell: () => ({ className: "month-days collapsed month-start month-end" }),
                render: () => null,
              },
            ],
          };
        }

        const dayColumns = Array.from({ length: month.days }, (_, dayIndex) => {
          const date = new Date(year, monthIndex, dayIndex + 1);
          const formattedDate = date.toLocaleDateString("sv-SE");
          const dayOfWeek = date.getDay();
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

          const headerNode = (
            <div className="day-header">
              <div className="day-num">{dayIndex + 1}</div>
              <div className="day-dow">{weekday2(date)}</div>
            </div>
          );

          return {
            title: headerNode,
            key: `${month.name}-${dayIndex + 1}`,
            dataIndex: `${month.name}-${dayIndex + 1}`,
            width: 24,
            onHeaderCell: () => ({
              className: `month-days month-day-header ${isWeekend ? "weekend" : ""} ${dayIndex === 0 ? "month-start" : ""} ${dayIndex === month.days - 1 ? "month-end" : ""}`,
              ref:
                dayIndex === 0
                  ? (el: HTMLTableCellElement | null) => {
                      monthRefs.current[monthIndex] = el;
                    }
                  : undefined,
            }),
            onCell: () => {
              const cellMeta = getCellMeta(date, formattedDate);
              return {
                className: `${cellMeta.className} ${dayIndex === 0 ? "month-start" : ""} ${dayIndex === month.days - 1 ? "month-end" : ""}`,
                onClick: cellMeta.onClick,
                style: { cursor: cellMeta.cursor },
              };
            },
            render: () => {
              const cellMeta = getCellMeta(date, formattedDate);
              if (!cellMeta.popupContent) return null;
              return (
                <Tooltip title={cellMeta.popupContent}>
                  <span className="cell-anchor" />
                </Tooltip>
              );
            },
          };
        });

        return {
          title: monthTitle,
          key: `month-${month.name}`,
          onHeaderCell: () => ({
            className: "months",
            onClick: () => toggleMonth(month.name),
          }),
          children: dayColumns,
        };
      },
    );

    return [...baseColumns, ...monthColumns];
  }, [collapsedMonths, infoBtnMsg, monthsList, toggleMonth, year, getCellMeta]);

  // Scroll to the current month once on mount/year change - deliberately
  // NOT re-run on collapsedMonths, otherwise every manual expand/collapse
  // click snaps the view back to the current month instead of following
  // whatever month the user just expanded (see the same fix in
  // AnnualLeaveTable.tsx for the full explanation).
  useEffect(() => {
    const monthRef = monthRefs.current[currentMonthIndex];
    const scrollContainer =
      document.querySelector(".calendar-container .ant-table-content") ||
      document.querySelector(".calendar-container");

    if (!monthRef || !scrollContainer) return;

    const frozenWidth = 260;

    const runScroll = () => {
      const scrollLeft =
        monthRef.getBoundingClientRect().left -
        (scrollContainer as HTMLElement).getBoundingClientRect().left +
        (scrollContainer as HTMLElement).scrollLeft -
        frozenWidth;

      (scrollContainer as HTMLElement).scrollTo({
        left: scrollLeft,
        behavior: "smooth",
      });
    };

    const rafId = requestAnimationFrame(runScroll);
    return () => cancelAnimationFrame(rafId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonthIndex, annualLeaveYear, monthsList]);

  const rowData = useMemo<CalendarRow[]>(() => {
    const employeeName = leaveRequests[0]?.employeeName || "";
    const daysRemaining = leaveRequests[0]?.currentYearRemainingDays ?? "";
    return [
      {
        key: "annual-leave-row",
        employeeName,
        daysRemaining,
      },
    ];
  }, [leaveRequests]);

  return (
    <div className="al-years-table">
      <div className="calendar-container">
  
        <Table
          className="calendar-table"
          columns={columns}
          loading={loading}
          dataSource={rowData}
          pagination={false}
          rowClassName={() => "calendar-user-data"}
          // x: true keeps header/body columns in lockstep - see the same
          // fix and rationale in AnnualLeaveTable.tsx.
          scroll={{ x: true }}
          size="small"
        />
      </div>
    </div>
  );
};

export default AnnualLeaveSingleTable;
